import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { trackEvent } from '../Analytics';

const SatiLogo = ({ visible = true, inverted = false, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-[var(--sati-control-h)] w-full min-w-0 max-w-full shrink-0 items-center justify-start bg-transparent border-none cursor-pointer p-0 md:w-auto md:justify-center"
      tabIndex={visible ? 0 : -1}
      aria-label="SATI Architects — home"
      data-testid="header-logo"
    >
      <img
        src="/sati-logo.svg"
        alt="SATI Architects logo"
        className="h-auto min-h-0 min-w-0 w-auto max-h-[1.25rem] max-w-full object-contain object-center sm:max-h-[1.35rem] md:max-h-[1.65rem] lg:max-h-8 xl:max-h-[2.125rem] 2xl:max-h-[2.25rem]"
        style={{
          filter: inverted ? 'invert(1)' : 'none',
          opacity: loaded && visible ? 1 : 0,
          transition: 'opacity 0.55s cubic-bezier(0.33, 1, 0.68, 1), filter 0.35s ease',
          pointerEvents: visible ? 'auto' : 'none',
        }}
        onLoad={() => setLoaded(true)}
      />
    </button>
  );
};

const LanguageSwitcher = ({ isLight }) => {
  const { language, toggleLanguage } = useLanguage();

  const pick = (code) => {
    toggleLanguage(code);
    trackEvent('language_switch', { to: code });
  };

  return (
    <div
      className={`inline-flex h-[var(--sati-control-h)] shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0 text-[10px] tracking-[0.1em] shadow-[0_2px_16px_rgba(0,0,0,0.2)] ${
        isLight ? 'border-white/35 bg-black/15' : 'border-foreground/12 bg-background/40'
      }`}
    >
      <button
        type="button"
        onClick={() => pick('UA')}
        className={`h-9 min-w-[2.25rem] rounded-full flex items-center justify-center leading-none transition-all duration-300 ${
          language === 'UA'
            ? isLight
              ? 'bg-white/15 text-white'
              : 'bg-foreground/8 text-foreground'
            : isLight
              ? 'text-white/62 hover:text-white/95'
              : 'text-foreground/40 hover:text-foreground/70'
        }`}
        data-testid="lang-ua"
      >
        UA
      </button>
      <button
        type="button"
        onClick={() => pick('EN')}
        className={`h-9 min-w-[2.25rem] rounded-full flex items-center justify-center leading-none transition-all duration-300 ${
          language === 'EN'
            ? isLight
              ? 'bg-white/15 text-white'
              : 'bg-foreground/8 text-foreground'
            : isLight
              ? 'text-white/62 hover:text-white/95'
              : 'text-foreground/40 hover:text-foreground/70'
        }`}
        data-testid="lang-en"
      >
        EN
      </button>
    </div>
  );
};

const Navigation = ({ heroIntroComplete = false }) => {
  const [isOnHero, setIsOnHero] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const heroObserverRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const tryObserve = () => {
      const heroEl = document.getElementById('hero');
      if (!heroEl) return false;
      heroObserverRef.current = new IntersectionObserver(
        ([entry]) => setIsOnHero(entry.isIntersecting),
        { threshold: 0.08, rootMargin: '-8px 0px 0px 0px' }
      );
      heroObserverRef.current.observe(heroEl);
      return true;
    };

    if (!tryObserve()) {
      const retry = setTimeout(tryObserve, 200);
      return () => clearTimeout(retry);
    }

    return () => heroObserverRef.current?.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isHomePage = location.pathname === '/';
  const isInnerPage = !isHomePage;
  const isLight = isHomePage && isOnHero;

  const scrollToSectionId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSectionNav = (e, hash) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!id) return;
    if (location.pathname === '/') {
      scrollToSectionId(id);
      return;
    }
    navigate({ pathname: '/', hash: id });
  };

  const sectionHref = (hash) => {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    return id ? `/#${id}` : hash;
  };

  const handleLogoClick = (e) => {
    if (e) e.preventDefault();
    if (!isHomePage) {
      navigate('/');
      window.scrollTo({ top: 0 });
    } else {
      const hero = document.getElementById('hero');
      if (hero) hero.scrollIntoView({ behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const leftNav = [
    { href: '#about', label: t.nav.philosophy },
    { href: '#projects', label: t.nav.projects },
    { href: '#process', label: t.nav.process },
  ];

  const rightNav = [
    { to: '/estimate', label: t.nav.projectBuilder, secondary: true },
    { href: '#contact', label: t.nav.contact },
  ];

  const estimatePathActive = location.pathname === '/estimate';

  const desktopNavClasses = (isEstimateLink, secondary) => {
    const active = isEstimateLink && estimatePathActive;
    const sub = secondary && !active;
    const base =
      'inline-flex min-h-0 shrink-0 items-center justify-center h-[var(--sati-control-h)] px-1.5 md:px-1.5 lg:px-2.5 xl:px-3 text-[8px] md:text-[8px] lg:text-[9px] xl:text-[10px] tracking-[0.07em] md:tracking-[0.08em] lg:tracking-[0.09em] xl:tracking-[0.11em] uppercase leading-none transition-all duration-400 relative group whitespace-nowrap';
    if (isLight) {
      return `${base} [text-shadow:0_1px_14px_rgba(0,0,0,0.55)] ${
        active ? 'text-white' : sub ? 'text-white/58 hover:text-white/95' : 'text-white/90 hover:text-white'
      }`;
    }
    return `${base} ${
      active
        ? 'text-foreground font-medium'
        : sub
          ? 'text-foreground/42 hover:text-foreground/78'
          : 'text-foreground/72 hover:text-foreground'
    }`;
  };

  const desktopUnderline = (isEstimateLink) => {
    const active = isEstimateLink && estimatePathActive;
    return `absolute bottom-1 left-0 h-px bg-current opacity-45 transition-all duration-300 ${
      active ? 'w-full' : 'w-0 group-hover:w-full'
    }`;
  };

  const NavItemDesktop = ({ item }) => {
    const isEstimateLink = Boolean(item.to);
    const cls = desktopNavClasses(isEstimateLink, item.secondary);
    const line = desktopUnderline(isEstimateLink);

    if (item.to) {
      return (
        <Link
          to={item.to}
          className={cls}
          data-testid="nav-project-builder"
          onClick={() => trackEvent('click_project_builder', { from_path: location.pathname })}
        >
          {item.label}
          <span className={`${line} pointer-events-none`} aria-hidden />
        </Link>
      );
    }
    return (
      <a
        href={sectionHref(item.href)}
        onClick={(e) => handleSectionNav(e, item.href)}
        className={cls}
      >
        {item.label}
        <span className={`${line} pointer-events-none`} aria-hidden />
      </a>
    );
  };

  const headerSurface = isLight
    ? 'bg-gradient-to-b from-black/48 via-black/18 to-transparent backdrop-blur-[8px]'
    : isInnerPage
      ? 'bg-[hsl(var(--ivory-mist))] backdrop-blur-sm shadow-[0_1px_0_hsl(var(--foreground)/0.11)]'
      : 'bg-[hsl(var(--ivory-mist)/0.96)] backdrop-blur-md shadow-[0_1px_0_hsl(var(--foreground)/0.06)]';

  const headerBorder = isLight
    ? '1px solid rgba(255,255,255,0.12)'
    : isInnerPage
      ? '1px solid hsl(var(--foreground) / 0.1)'
      : '1px solid hsl(var(--foreground) / 0.06)';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${headerSurface}`}
        style={{ borderBottom: headerBorder }}
        data-sati-header={isInnerPage ? 'inner' : isLight ? 'hero' : 'home-scrolled'}
        data-testid="header"
      >
        <nav className="arch-container relative z-10 flex min-h-[var(--sati-header-h)] flex-col justify-center">
          {/* Centered wordmark + side bands capped at calc(50% - reserve): prevents logo/nav collision at any width */}
          <div className="relative hidden min-h-[var(--sati-control-h)] w-full items-center py-2.5 md:flex md:justify-center">
            <div className="sati-header-side-left absolute inset-y-0 left-0 z-[2] flex min-w-0 items-center gap-x-2 overflow-x-auto overflow-y-visible [-ms-overflow-style:none] [scrollbar-width:none] md:gap-x-2.5 lg:gap-x-3 xl:gap-x-4 2xl:gap-x-5 [&::-webkit-scrollbar]:hidden">
              {leftNav.map((link) => (
                <NavItemDesktop key={link.to || link.href} item={link} />
              ))}
            </div>

            <div className="relative z-[1] flex max-w-[min(86vw,16.5rem)] shrink-0 justify-center overflow-hidden px-2 sm:max-w-[min(84vw,17rem)] md:max-w-[min(78vw,17.5rem)] lg:max-w-[18rem] xl:max-w-[19rem] 2xl:max-w-[20rem]">
              <SatiLogo visible={heroIntroComplete} inverted={isLight} onClick={handleLogoClick} />
            </div>

            <div className="sati-header-side-right absolute inset-y-0 right-0 z-[2] flex min-w-0 items-center justify-start gap-x-2 overflow-x-visible overflow-y-visible md:gap-x-2.5 lg:gap-x-3 xl:gap-x-4 2xl:gap-x-5">
              {rightNav.map((link) => (
                <NavItemDesktop key={link.to || link.href} item={link} />
              ))}
              <LanguageSwitcher isLight={isLight} />
              <a
                href={sectionHref('#contact')}
                onClick={(e) => {
                  trackEvent('click_contact_cta', { placement: 'header' });
                  handleSectionNav(e, '#contact');
                }}
                className={`hidden shrink-0 lg:inline-flex h-[var(--sati-control-h)] items-center justify-center whitespace-nowrap rounded-sm px-3 text-[10px] font-semibold uppercase leading-none tracking-[0.12em] transition-all duration-300 hover:-translate-y-px active:translate-y-0 xl:px-4 xl:tracking-[0.14em] ${
                  isLight
                    ? 'bg-[hsl(var(--ivory-mist))] text-[hsl(var(--charcoal-black))] shadow-[0_6px_28px_rgba(0,0,0,0.35)] ring-1 ring-white/25 hover:brightness-[1.03]'
                    : 'bg-foreground text-background shadow-md ring-1 ring-foreground/10 hover:opacity-[0.94]'
                }`}
                data-testid="header-cta"
              >
                {t.nav.cta}
              </a>
            </div>
          </div>

          <div className="flex min-h-[var(--sati-control-h)] items-center gap-4 py-2 md:hidden">
            <div className="flex min-w-0 flex-1 items-center justify-start pr-2">
              <SatiLogo
                visible={heroIntroComplete}
                inverted={isLight && !isMobileMenuOpen}
                onClick={handleLogoClick}
              />
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <LanguageSwitcher isLight={isLight && !isMobileMenuOpen} />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="relative z-50 inline-flex h-[var(--sati-control-h)] w-[var(--sati-control-h)] flex-col items-center justify-center"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                <span
                  className={`w-5 h-px transition-all duration-400 absolute ${
                    isMobileMenuOpen ? 'rotate-45' : '-translate-y-1'
                  }`}
                  style={{
                    backgroundColor: isMobileMenuOpen
                      ? 'hsl(var(--foreground))'
                      : isLight
                        ? 'rgba(255,255,255,0.92)'
                        : 'hsl(var(--foreground))',
                  }}
                />
                <span
                  className={`w-5 h-px transition-all duration-400 absolute ${
                    isMobileMenuOpen ? '-rotate-45' : 'translate-y-1'
                  }`}
                  style={{
                    backgroundColor: isMobileMenuOpen
                      ? 'hsl(var(--foreground))'
                      : isLight
                        ? 'rgba(255,255,255,0.92)'
                        : 'hsl(var(--foreground))',
                  }}
                />
              </button>
            </div>
          </div>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-background transition-all duration-400 ease-out md:hidden ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <nav className="space-y-7 text-center max-w-sm">
            {[...leftNav, ...rightNav].map((link, index) => {
              const key = link.to || link.href;
              const isEstimate = Boolean(link.to);
              const mobileActive = isEstimate && estimatePathActive;
              const secondaryCls = link.secondary ? ' text-foreground/48' : '';
              return (
                <div key={key} className="overflow-hidden">
                  {link.to ? (
                    <Link
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block text-2xl font-display text-foreground min-h-[44px] flex items-center justify-center${
                        mobileActive ? ' underline underline-offset-[6px]' : ''
                      }${secondaryCls}`}
                      style={{
                        transform: `translateY(${isMobileMenuOpen ? 0 : 100}%)`,
                        transition: `transform 0.65s cubic-bezier(0.16, 1, 0.3, 1) ${0.18 + index * 0.05}s`,
                      }}
                      data-testid="nav-project-builder-mobile"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={sectionHref(link.href)}
                      onClick={(e) => handleSectionNav(e, link.href)}
                      className="block text-2xl font-display text-foreground min-h-[44px] flex items-center justify-center"
                      style={{
                        transform: `translateY(${isMobileMenuOpen ? 0 : 100}%)`,
                        transition: `transform 0.65s cubic-bezier(0.16, 1, 0.3, 1) ${0.18 + index * 0.05}s`,
                      }}
                    >
                      {link.label}
                    </a>
                  )}
                </div>
              );
            })}
            <div className="pt-6">
              <a
                href={sectionHref('#contact')}
                onClick={(e) => handleSectionNav(e, '#contact')}
                className="inline-flex items-center justify-center px-10 h-[var(--sati-control-h)] min-h-[48px] text-[10px] tracking-[0.16em] uppercase bg-foreground text-background rounded-sm shadow-md transition-opacity duration-500 hover:opacity-95"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transition: 'opacity 0.45s ease 0.45s',
                }}
              >
                {t.nav.cta}
              </a>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export { Navigation, SatiLogo, LanguageSwitcher };
